/**
 * Course-to-steer builder, the tidal triangle. Tap the start, tap the
 * destination, check the boat speed and the tide, and the app solves the
 * heading to steer to make the track good, draws the triangle, and reports the
 * speed over the ground and the time.
 *
 * The four quantities are kept distinct in the result, because confusing them
 * is the classic error: the course to steer through the water, the desired
 * ground track over the ground, the tidal vector, and the bearing to the
 * destination. If the tide is too strong to make the track at all, we say so
 * plainly rather than drawing a nonsense triangle.
 */
import { useState } from 'react';
import { useShellStore } from '@/store/shell';
import { useChartPlotterStore } from '@/store';
import { useEnvStore } from '@/store/environment';
import { useObjectsStore, nextObjectId } from '@/store/objects';
import { buildCTS } from '@/chartwork';
import { sampleTide } from '@/environment';
import { formatBearing } from '@/nav';
import { Sheet } from './Sheet';

export function CtsSheet() {
  const pendingMark = useShellStore((s) => s.pendingMark);
  const armFixPick = useShellStore((s) => s.armFixPick);
  const setPendingMark = useShellStore((s) => s.setPendingMark);
  const closeSheet = useShellStore((s) => s.closeSheet);
  const showToast = useShellStore((s) => s.showToast);
  const ownship = useChartPlotterStore((s) => s.ownship);
  const tide = useEnvStore((s) => s.tide);
  const clockH = useEnvStore((s) => s.clockH);
  const streamNow = sampleTide(tide, clockH);

  const [startPt, setStartPt] = useState<[number, number] | null>(null);
  const [speed, setSpeed] = useState(() => String(ownship.stw || ownship.sog || 6));
  const [set, setSet] = useState(() => String(Math.round(streamNow.setDeg)));
  const [rate, setRate] = useState(() => streamNow.rateKn.toFixed(1));
  const [leeway, setLeeway] = useState('');

  function reset() {
    setStartPt(null);
    setPendingMark(null);
  }

  function compute() {
    const sp = Number.parseFloat(speed);
    const st = Number.parseFloat(set);
    const rt = Number.parseFloat(rate);
    const lw = leeway.trim() === '' ? 0 : Number.parseFloat(leeway);
    if (!startPt || !pendingMark || [sp, st, rt, lw].some(Number.isNaN)) {
      showToast('Pick start and destination, and check the figures');
      return;
    }
    const built = buildCTS({
      start: startPt,
      to: pendingMark.position,
      boatSpeed: sp,
      tidalSet: st,
      tidalRate: rt,
      leewayDeg: lw,
      time: { iso: new Date().toISOString() },
      newId: nextObjectId,
    });
    if (built.result.unachievable || !built.objects) {
      showToast('Tide too strong to make that track at this speed');
      return;
    }
    useObjectsStore.getState().putObjects(built.objects);
    const r = built.result;
    const h = Math.floor(r.hours);
    const m = Math.round((r.hours - h) * 60);
    reset();
    closeSheet();
    showToast(
      `CTS ${formatBearing(r.ctsDeg)}, SOG ${r.sogKnots.toFixed(1)} kn, ${h}h ${String(m).padStart(2, '0')}`,
    );
  }

  return (
    <Sheet
      name="cts"
      title="Course to steer"
      sub="Tap the start, then the destination. We solve the heading to steer so the tide carries us onto the track, and show the speed over the ground and the time. Tide is from the scenario; adjust if you like."
    >
      <div className="acp-kv">
        <span className="k">Start</span>
        <span className="v">{startPt ? 'set' : '—'}</span>
      </div>

      {!startPt ? (
        pendingMark ? (
          <div className="acp-btn-row">
            <button
              type="button"
              className="acp-btn primary"
              onClick={() => {
                setStartPt(pendingMark.position);
                setPendingMark(null);
              }}
            >
              Use as start
            </button>
          </div>
        ) : (
          <div className="acp-btn-row">
            <button
              type="button"
              className="acp-btn primary"
              onClick={() => {
                armFixPick('cts');
                showToast('Tap the start');
              }}
            >
              Pick start
            </button>
          </div>
        )
      ) : (
        <>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <Field label="Boat speed kn" value={speed} onChange={setSpeed} />
            <Field label="Tide set °" value={set} onChange={setSet} />
            <Field label="Tide rate kn" value={rate} onChange={setRate} />
            <Field label="Leeway °" value={leeway} onChange={setLeeway} />
          </div>
          <div className="acp-kv" style={{ marginTop: 8 }}>
            <span className="k">Destination</span>
            <span className="v">{pendingMark ? 'set' : '—'}</span>
          </div>
          {pendingMark ? (
            <div className="acp-btn-row">
              <button type="button" className="acp-btn" onClick={reset}>
                Start over
              </button>
              <button type="button" className="acp-btn primary" onClick={compute}>
                Solve course to steer
              </button>
            </div>
          ) : (
            <div className="acp-btn-row">
              <button type="button" className="acp-btn" onClick={reset}>
                Start over
              </button>
              <button
                type="button"
                className="acp-btn primary"
                onClick={() => {
                  armFixPick('cts');
                  showToast('Tap the destination');
                }}
              >
                Pick destination
              </button>
            </div>
          )}
        </>
      )}
    </Sheet>
  );
}

function Field({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label style={{ flex: 1, fontSize: 11, color: 'var(--ink-soft)' }}>
      {label}
      <input
        className="acp-note-input"
        style={{ marginTop: 2 }}
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}
